import { CopyDuplicateObject, MoreHorizontal, Trash } from '@griply/assets';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  ResizableTextArea,
  Variants as ResizableTextAreaVariants,
} from 'shared/components/ui/resizable-text-area';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from './event-form.style';

export type EventFormHeaderProps = {
  value: string;
  onChange?: (title: string) => void;
  disabled?: boolean;
  titleRef?: React.RefObject<HTMLTextAreaElement>;
  // the actions menu appears only for the handlers that are supplied — a create
  // popup has nothing to duplicate or delete yet, so it passes neither
  onDuplicate?: () => void;
  onDelete?: () => void;
};

export const EventFormHeader: React.FC<EventFormHeaderProps> = ({
  value,
  onChange,
  disabled,
  titleRef,
  onDuplicate,
  onDelete,
}) => {
  const { t } = useTranslation();
  const menuTriggerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const isReadOnly = !!disabled || !onChange;
  const hasMenu = !!onDuplicate || !!onDelete;

  const onSelectDuplicate = () => {
    closeMenu();
    onDuplicate?.();
  };

  const onSelectDelete = () => {
    closeMenu();
    onDelete?.();
  };

  return (
    <Styled.Header>
      <Styled.TitleColumn>
        {isReadOnly && <Styled.Title>{value}</Styled.Title>}
        {!isReadOnly && !!onChange && (
          <ResizableTextArea
            ref={titleRef}
            value={value}
            onChange={onChange}
            variant={ResizableTextAreaVariants.Subtitle2}
            placeholder={t('forms.event.fields.title.placeholder')}
            fullWidth
          />
        )}
      </Styled.TitleColumn>

      {hasMenu && (
        <Styled.HeaderMenuAnchor ref={menuTriggerRef}>
          <IconButton
            icon={MoreHorizontal}
            size={Sizes.Small}
            color={Colors.Default}
            onClick={openMenu}
            type="button"
            aria-label={t('context-menus.external-event.labels.more')}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          />
          {menuOpen && (
            <PopupMenu
              referenceElement={menuTriggerRef}
              onOutside={closeMenu}
              position="bottom-end"
            >
              <PopupMenuList>
                {!!onDuplicate && (
                  <PopupMenuListItem>
                    <PopupMenuButton
                      onClick={onSelectDuplicate}
                      start={<Icon icon={CopyDuplicateObject} />}
                    >
                      {t('context-menus.external-event.labels.duplicate')}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                )}
                {!!onDelete && (
                  <PopupMenuListItem>
                    <PopupMenuButton
                      onClick={onSelectDelete}
                      start={<Icon icon={Trash} />}
                    >
                      {t('context-menus.external-event.labels.delete')}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                )}
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.HeaderMenuAnchor>
      )}
    </Styled.Header>
  );
};
